import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AttendanceCode } from '../../classroom360/attendance/models/attendance-code.model';
import { SharedService } from '../shared.service';
import { YearService } from '../year/year.service';
import { System } from './../System';
import { CityStateZipCodeLoookup } from './city-state-zip-code-lookup.model';
import { GradeLevelLookup } from './grade-level-lookup.model';
import { StudentRegistrationType } from './lookup.enums';
import { DistrictLookup, Lookup, SiteLookup } from './lookup.model';
/**
 * This service can be used to obtain a variety of lookups.
 * The lookup model is the basis for these lookups.
 * If you need to get lookups, try to add them here instead of in another service.
 */
@Injectable()
export class LookupService {
	/**
	 * Note: A lot of these classes check that the default year is set
	 * This is because these lookups are also used in family registration
	 * Those are the only two known uses cases, though, so a parameter was deemed unnecessary
	 */

	// Properties
	gradeLevelLookups: GradeLevelLookup[] = [];

	// Class Functions
	constructor(
		private http: HttpClient,
		private yearService: YearService,
		private sharedService: SharedService
	) { }

	// Event Functions

	// Methods
	getRelationshipLookup(ayIdentifier = this.yearService.defaultYear.AYIdentifier): Observable<Lookup[]> {
		// POST: obtains the relationships for the current AYIdentifier (parent?)
		const queryString = '?ayIdentifier=' + ayIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetRelationshipLookup' + queryString);
	}

	getLanguageLookup(): Observable<Lookup[]> {
		// POST: obtains the list of all languages supported or to be supported
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetLanguageLookup');
	}

	getMaritalStatusLookup(): Observable<Lookup[]> {
		// POST: obtains the list of all martial statues supported or to be supported
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetMaritalStatusLookup');
	}

	getStreetDirectionLookup(ayIdentifier = this.yearService.defaultYear.AYIdentifier): Observable<Lookup[]> {
		// POST: obtains the list of street directions supported or to be supported
		const queryString = '?ayIdentifier=' + ayIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetStreetDirectionLookup' + queryString);
	}

	getStreetTypeLookup(ayIdentifier = this.yearService.defaultYear.AYIdentifier): Observable<Lookup[]> {
		// POST: obtains the list of street types supported or to be supported
		const queryString = '?ayIdentifier=' + ayIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetStreetTypeLookup' + queryString);
	}

	getCountryStateLookup(): Observable<Lookup[]> {
		// POST: obtains the list of states for the user's country
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetCountryStateLookup');
	}

	getCountryLookup(ayIdentifier = this.yearService.defaultYear.AYIdentifier): Observable<Lookup[]> {
		// POST: obtains the list of supported countries
		const queryString = '?ayIdentifier=' + ayIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetCountryLookup' + queryString);
	}

	getEthnicityLookup(): Observable<Lookup[]> {
		// POST: obtains the list of ethnicities
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetEthnicityTypeLookup');
	}

	getGenderLookup(): Observable<Lookup[]> {
		// POST: obtains the list of genders
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetGenderTypeLookup');
	}

	getRaceLookup(addBlankItem: boolean = false): Observable<Lookup[]> {
		// POST: obtains the list of races
		const queryString = '?addBlankItem=' + addBlankItem;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetRaceTypeLookup' + queryString);
	}

	getSuffixLookup(ayIdentifier = this.yearService.defaultYear.AYIdentifier): Observable<Lookup[]> {
		// POST: obtains the list of suffixes (for names)
		const queryString = '?ayIdentifier=' + ayIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetSuffixLookup' + queryString);
	}

	getLivesWithLookup(): Observable<Lookup[]> {
		// POST: obtains the list of lives with (???)
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetLivesWithLookup');
	}

	getGradeLevelLookup(QRFamily?: boolean, yearId = this.yearService.defaultYear.Id, ayIdentifier = this.yearService.defaultYear.AYIdentifier): Observable<Lookup[]> {
		// PRE: A QRFamiy number
		// POSt: obtains the grade levels that are open for OR in the selected year
		const queryString = '?yearId=' + yearId +
			'&QRFamily=' + (QRFamily ? QRFamily : false) +	// You need this in case of undefined
			'&ayIdentifier=' + ayIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetORGradeLevelLookup' + queryString);
	}

	getMedicalConditionLookup(ayIdentifier = this.yearService.defaultYear.AYIdentifier): Observable<Lookup[]> {
		// POST: obtains the list of medical conditions
		const queryString = '?ayIdentifier=' + ayIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetMedicalConditionLookup' + queryString);
	}

	getDataDefCode(datadefType: number): Observable<Lookup[]> {
		const queryString = '?datadefType=' + datadefType;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetDataDefCodes' + queryString);
	}

	getCityStateZipCodeLookup(): Observable<CityStateZipCodeLoookup[]> {
		// POST: obtain the list of cities and states
		return this.http.get<CityStateZipCodeLoookup[]>(System.apiURL.Student + 'GetCityStateZipCodeLookup');
	}

	getAttedanceCodeLookup(isTeacher = false): Observable<AttendanceCode[]> {
		// PRE: isTeacher is true if the caller is a teacher
		// POST: obtains a list of attendance codes
		return this.sharedService.getAttendanceCodeLookup(false, this.yearService.defaultYear.SchoolID, this.yearService.defaultYear.Id);
	}

	getDisciplineReferralFunctionalMotivationLookup(): Observable<Lookup[]> {
		// POST: obtains the functional motivation lookups
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetDisciplineReferralFunctionalMotivationLookup');
	}

	getDisciplineReferralContextLookup(
		yearId = this.yearService.defaultYear.Id,
		schoolId = this.yearService.defaultYear.SchoolID
	): Observable<Lookup[]> {
		// POST: obtains a list of contexts for discipline referral
		const queryString = '?type=context' +
			'&yearId=' + yearId +
			'&schoolId=' + schoolId;
		return this.http.get<Lookup[]>(System.apiURL.Counselor + 'GetDisciplineReferralLookups' + queryString);
	}

	getDisciplineReferralReporterLookup(
		yearId = this.yearService.defaultYear.Id,
		schoolId = this.yearService.defaultYear.SchoolID
	): Observable<Lookup[]> {
		// POST: obtains a list of reporters for discipline referral
		const queryString = '?type=reportedBy' +
			'&yearId=' + yearId +
			'&schoolId=' + schoolId;
		return this.http.get<Lookup[]>(System.apiURL.Counselor + 'GetDisciplineReferralLookups' + queryString);
	}

	getDisciplineReferralSchoolLookup(
		yearId = this.yearService.defaultYear.Id,
		schoolId = this.yearService.defaultYear.SchoolID
	): Observable<Lookup[]> {
		// POST: obtains a list of schools for discipline referral
		const queryString = '?type=Schools' +
			'&yearId=' + yearId +
			'&schoolId=' + schoolId;
		return this.http.get<Lookup[]>(System.apiURL.Counselor + 'GetDisciplineReferralLookups' + queryString);
	}

	getDisciplineReferralPlaceLookup(
		schoolId = this.yearService.defaultYear.SchoolID
	): Observable<Lookup[]> {
		// POST: obtains a list of places for discipline referral
		const queryString = '?schoolId=' + schoolId;
		return this.http.get<Lookup[]>(System.apiURL.Counselor + 'GetDisciplineReferralPlacesLookup' + queryString);
	}

	getDisciplineReferralIncidentCodeLookup(
		yearId = this.yearService.defaultYear.Id, profileArea: string = 'Discipline'
	): Observable<Lookup[]> {
		// POST: obtains a list of places for discipline referral
		let queryString = '?yearId=' + yearId;
		queryString += '&schoolId=' + this.yearService.defaultYear.SchoolID;
		queryString += '&profileArea=' + profileArea;
		return this.http.get<Lookup[]>(System.apiURL.Counselor + 'GetDisciplineReferralIncidentLookup' + queryString);
	}

	getCommunicationLogTypesLookup(ayIdentifier: string): Observable<Lookup[]> {
		let paramString = '';
		paramString += '?ayIdentifier=' + ayIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetCommunicationLogTypesLookup' + paramString);
	}

	getCommunicationLogPersonCodesLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetCommunicationLogPersonCodesLookup');
	}

	getCommunicationLogResultLookup(ayIdentifier: string): Observable<Lookup[]> {
		let paramString = '';
		paramString += '?ayIdentifier=' + ayIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetCommunicationLogResultLookup' + paramString);
	}

	getORFormDocumentType(
		schoolId = this.yearService.defaultYear.SchoolID
	) {
		let paramString = '';
		paramString += '?schoolId=' + schoolId;
		return this.http.get<any>(System.apiURL.Student + 'GetORFormDocumentType' + paramString);
	}
	getincidentCodeDetailLookup(): Observable<Lookup[]> {
		// POST: obtains a list of Functional Motivation  for discipline event
		const queryString = '?type=incidentCodeDetail' +
			'&yearId=' + this.yearService.defaultYear.Id +
			'&schoolId=' + this.yearService.defaultYear.SchoolID;
		return this.http.get<Lookup[]>(System.apiURL.Counselor + 'GetDisciplineReferralLookups' + queryString);
	}
	getAdditionFieldsLookup(): Observable<Lookup[]> {
		// POST: obtains a list of Functional Motivation  for discipline event
		const queryString = '?type=incidentStateReporting' +
			'&yearId=' + this.yearService.defaultYear.Id +
			'&schoolId=' + this.yearService.defaultYear.SchoolID;
		return this.http.get<Lookup[]>(System.apiURL.Counselor + 'GetDisciplineReferralLookups' + queryString);
	}
	getIncidentWeaponLookup(): Observable<Lookup[]> {
		// POST: obtains a list of Functional Motivation  for discipline event
		const queryString = '?type=weaponType' +
			'&yearId=' + this.yearService.defaultYear.Id +
			'&schoolId=' + this.yearService.defaultYear.SchoolID;
		return this.http.get<Lookup[]>(System.apiURL.Counselor + 'GetDisciplineReferralLookups' + queryString);
	}

	getStudentRegCodes(regType: StudentRegistrationType, ayIdentifier: string): Observable<Lookup[]> {
		// POST: obtains the relationships for the current AYIdentifier (parent?)
		const queryString = '?regType=' + regType + '&ayIdentifier=' + ayIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetStudentRegCodes' + queryString);
	}

	getCreditCodes(ayIdentifier = this.yearService.defaultYear.AYIdentifier): Observable<Lookup[]> {
		// POST: obtains the relationships for the current AYIdentifier (parent?)
		const queryString = '?ayIdentifier=' + ayIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetCreditCodes' + queryString);
	}

	getDataQueryFieldLookupReadByFieldId(fieldID: number, categoryID: number): Observable<Lookup[]> {
		// PRE: a data query field's ID and category's ID
		// POST: obtains the field options by the field ID
		const queryString = '?dataQueryFieldId=' + fieldID +
			'&dataQueryCategoryId=' + categoryID;
		return this.http.get<Lookup[]>(System.apiURL.Classroom + 'GetDataQueryFieldLookupReadByFieldId' + queryString);
	}

	getAllGradeLevels(QRFamily?: boolean, yearId = this.yearService.defaultYear.Id, ayIdentifier = this.yearService.defaultYear.AYIdentifier): Observable<Lookup[]> {
		// POST: V10 obtains all grade levels from sysGradeLevel
		const queryString = '?yearId=' + yearId +
			'&QRFamily=' + (QRFamily ? QRFamily : false) +	// You need this in case of undefined
			'&ayIdentifier=' + ayIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetAllGradeLevels' + queryString);
	}

	getStudentListScopes(): Observable<Lookup[]> {
		// POST: obtains all the student list scopes
		const queryString = '?regType=' + 1582 +
			'&ayIdentifier=' + this.yearService.defaultYear.AYIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetStudentRegCodes' + queryString);
	}

	getStudentListTypes(): Observable<Lookup[]> {
		// POST: obtains all the student list scopes
		const queryString = '?regType=' + 1581 +
			'&ayIdentifier=' + this.yearService.defaultYear.AYIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetStudentRegCodes' + queryString);
	}

	getCommunicationLogReasons(): Observable<Lookup[]> {
		// POST: obtains all the student list scopes
		const queryString = '?regType=' + 1600 +
			'&ayIdentifier=' + this.yearService.defaultYear.AYIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetStudentRegCodes' + queryString);
	}

	getCommunicationLogPreDefinedConfidentialNote(): Observable<Lookup[]> {
		// POST: obtains all the student list scopes
		const queryString = '?regType=' + 1611 +
			'&ayIdentifier=' + this.yearService.defaultYear.AYIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetStudentRegCodes' + queryString);
	}

	getCommunicationLogPreDefinedComment(): Observable<Lookup[]> {
		// POST: obtains all the student list scopes
		const queryString = '?regType=' + 1610 +
			'&ayIdentifier=' + this.yearService.defaultYear.AYIdentifier;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetStudentRegCodes' + queryString);
	}

	getStaffRolesLookup(): Observable<Lookup[]> {
		// POST: obtains all staff roles
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetRolesLookup');
	}

	getStudentPortfolioCategoryLookup(): Observable<Lookup[]> {
		// POST: obtains all student portfolio categories
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetStudentPortfolioCategoryLookup');
	}

	getDocumentTypeLookup(): Observable<Lookup[]> {
		// POST: obtains all student digital content document types
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetDocumentTypeLookup');
	}

	getRolesForEForms(): Observable<Lookup[]> {
		// POST: gets non-system roles as well as e-Form specific system roles
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetRolesForEForms');
	}

	getSubjectTermTypeLookup(): Observable<Lookup[]> {
		// POST: obtains all student digital content document types
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetSubjectTermTypeLookup');
	}

	getIncidentSubCodes(incidentCodeId: number): Observable<Lookup[]> {
		let queryString = '';
		queryString += '?lngIncidentCodeId=' + incidentCodeId;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetIncidentSubCodesLookup' + queryString);
	}

	getStateReportingCodes(type: number, stateReportingCodesId: number, stateCode: string, useSequence: boolean): Observable<Lookup[]> {
		let queryString = '';
		queryString += '?type=' + type +
			'&stateReportingCodesId=' + stateReportingCodesId +
			'&stateCode=' + stateCode +
			'&useSequence=' + useSequence;
		return this.http.get<Lookup[]>(System.apiURL.Admin + 'GetStateReportingCodes' + queryString);
	}

	getGradeLevelsLookup(): Observable<GradeLevelLookup[]> {
		return this.http.get<GradeLevelLookup[]>(System.apiURL.Student + 'GetGradeLevelsLookup');
	}

	getDepartmentCodes(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Admin + 'GetDistrictDepartmentsCodes');
	}

	getPhysicianTypeList(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Student + `GetPhysicianTypeLookUp`);
	}

	getVaccineTypesLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetVaccineTypesLookup');
	}

	getExemptCodeLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetExemptCodeLookup');
	}

	getTestMastersLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetTestMastersLookup');
	}

	getDistrictLookup(): Observable<DistrictLookup[]> {
		return this.http.get<DistrictLookup[]>(System.apiURL.Counselor + 'GetDistrictLookup');
	}

	getSiteLookup(districtId: number): Observable<SiteLookup[]> {
		return this.http.get<SiteLookup[]>(System.apiURL.Counselor + `GetSiteLookup?districtId=${districtId}`);
	}

	getGradeLevelsReadBySchoolYear(schoolId: number, yearId: number): Observable<Lookup[]> {
		let queryString = '';
		queryString += '?schoolId=' + schoolId +
			'&yearId=' + yearId;
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetGradeLevelsReadBySchoolYear' + queryString);
	}

	getPermissionGroupLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Student + 'GetPermissionGroupLookup');
	}
	//
	getTerminationReasionLookup(state: string, yearId: number): Observable<Lookup[]> {
		let queryString = '';
		queryString += '?state=' + state +
			'&yearId=' + yearId;
		return this.http.get<Lookup[]>(System.apiURL.Management + 'GetTerminationReasonLookUp' + queryString);
	}

	getWithdrawnCodesReadLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Counselor + `GetWithdrawnCodesReadLookup`);
	}

	getTeacherSpecialtyCodesLookUp(yearId: number): Observable<Lookup[]> {
		let queryString = '';
		queryString += '?yearId=' + yearId;
		return this.http.get<Lookup[]>(System.apiURL.Management + 'GetTeacherSpecialtyCodesLookUp' + queryString);
	}

	getClinicVisitCodesLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Health + `GetClinicVisitCodesLookup`);
	}

	getMedicalCodeClinicVisitNoShowLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Health + `GetMedicalCodeClinicVisitNoShowLookup`);
	}

	getDeliveryResultCodeLookUp(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Health + `GetDeliveryResultCodesLookup`);
	}

	getCourseGradingPurposeLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Counselor + `GetCourseGradingPurposeLookup`);
	}

	getTermGradeTypesLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Counselor + `GetTermGradeTypesLookup`);
	}

	getMedicationCodesLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Health + `GetMedicationCodesLookup`);
	}

	getMedicationFrequencyLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Health + `GetMedicationFrequencyLookup`);
	}

	getMedicationRoutesLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Health + `GetMedicationRoutesLookup`);
	}

	getPractionerLookUp(studentId: number): Observable<Lookup[]> {
		let queryString = '';
		queryString += '?studentId=' + studentId;
		return this.http.get<Lookup[]>(System.apiURL.Health + `GetPractionerLookup` + queryString);
	}

	getMedicalClinicLogSentStudentLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Health + `GetMedicalClinicLogSentStudentLookup`);
	}

	getMedicalClinicLogDOHSurveillanceLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Health + `GetMedicalClinicLogDOHSurveillanceLookup`);
	}

	getClinicLogVisitCodesLookup(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Health + `GetClinicLogVisitCodesLookup`);
	}

	getMealApplicationConfidentiallyWaiverLookups(): Observable<Lookup[]> {
		return this.http.get<Lookup[]>(System.apiURL.Student + `GetMealApplicationConfidentiallyWaiverLookups`);
	}
}
